import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, FileText, FileDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import AddBook from "../../modals/teacher/AddBook";
import EditBook from "../../modals/teacher/EditBook";
import book from "../../assets/images/book.png";
import { Table, Modal as AntModal, message, Pagination } from 'antd';
import GenerateExam from "../../modals/teacher/GenerateExams";
import { getRequest, deleteRequest } from "../../services/index";
import jsPDF from 'jspdf';

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto", 
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    border: "none",
  },
};

const paperData = [
  {
    id: 1,
    subject: "Mathematics",
    class: "10th",
    date: "2024-02-15",
    status: "Published"
  },
  {
    id: 2,
    subject: "Science",
    class: "9th", 
    date: "2024-02-16",
    status: "Draft"
  },
  {
    id: 3,
    subject: "English",
    class: "8th",
    date: "2024-02-17", 
    status: "Published"
  }
];

const ExamAutomation = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [generateExamModal, setGenerateExamModal] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [activeTab, setActiveTab] = useState('generate');
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "sa";
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [totalBooks, setTotalBooks] = useState(0);
  const [savedPapers, setSavedPapers] = useState([]);
  const [totalPapers, setTotalPapers] = useState(0);
  const [papersLoading, setPapersLoading] = useState(false);
  const [paperDetailsModal, setPaperDetailsModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);

  useEffect(() => {
    if (activeTab === 'generate') {
      fetchBooks();
    } else {
      fetchSavedPapers();
    }
  }, [currentPage, pageSize, activeTab]);

  const fetchBooks = async () => {
    try {
      setLoading(true);
      const response = await getRequest(`/api/PaperGeneration/books?pageNumber=${currentPage}&pageSize=${pageSize}`);
      if (response.data) {
        setBooks(response.data.items || []);
        setTotalBooks(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
      message.error('Failed to fetch books');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedPapers = async () => {
    try {
      setPapersLoading(true);
      const response = await getRequest(`/api/PaperGeneration/get-saved-papers?pageNumber=${currentPage}&pageSize=${pageSize}`);
      if (response.data) {
        setSavedPapers(response.data.items || []);
        setTotalPapers(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching saved papers:', error);
      message.error('Failed to fetch saved papers');
    } finally {
      setPapersLoading(false);
    }
  };

  const handleAddNewExamClick = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const closeGenerateExamModal = () => {
    setGenerateExamModal(false);
  };

  const closeEditModal = () => {
    setEditModalIsOpen(false);
    setSelectedExamId(null);
  };

  const handleBookClick = () => {
    setGenerateExamModal(true);
  };

  const handleEdit = (id) => {
    setSelectedExamId(id);
    setEditModalIsOpen(true);
  };

  const handleDelete = (id) => {
    setBookToDelete(id);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bookToDelete) return;
    
    setDeleteLoading(true);
    try {
      const response = await deleteRequest(`/api/PaperGeneration/delete-book/${bookToDelete}`);
      if (response.status === 200) {
        message.success('Book deleted successfully');
        fetchBooks(); // Refresh the books list
      } else {
        message.error('Failed to delete book');
      }
    } catch (error) {
      console.error('Error deleting book:', error);
      message.error('Failed to delete book');
    } finally {
      setDeleteLoading(false);
      setDeleteModalVisible(false);
      setBookToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setBookToDelete(null);
  };

  const handleGenerate = (id) => {
    setSelectedExamId(id);
    setGenerateExamModal(true);
  };

  const handleDownloadPDF = (record) => {
    try {
      const doc = new jsPDF();
      const paperData = JSON.parse(record.paperJson);
      
      // Add header information
      doc.setFontSize(16);
      doc.text(record.subjectName.toUpperCase(), 105, 20, { align: 'center' });
      
      doc.setFontSize(12);
      doc.text(`Class: ${record.className}`, 20, 30);
      doc.text(`Exam Type: ${record.examType}`, 20, 40);
      doc.text(`Total Marks: ${record.totalMarks}`, 20, 50);
      doc.text(`Question Type: ${record.questionType}`, 20, 60);
      
      // Add questions
      doc.setFontSize(14);
      doc.text('Questions:', 20, 80);
      
      let yPosition = 90;
      paperData.forEach((question, index) => {
        // Add question number and text
        doc.setFontSize(12);
        const questionText = `${index + 1}. ${question.question}`;
        const splitText = doc.splitTextToSize(questionText, 170);
        doc.text(splitText, 20, yPosition);
        
        // Update y position after question text
        yPosition += (splitText.length * 7) + 5;

        // If it's an MCQ question, add options
        if (question.options && Array.isArray(question.options)) {
          question.options.forEach((option, optIndex) => {
            const optionText = option.trim();
            if (optionText) {
              const splitOption = doc.splitTextToSize(optionText, 160);
              doc.text(splitOption, 30, yPosition);
              yPosition += (splitOption.length * 7) + 5;
            }
          });
        }
        
        // Add marks
        doc.text(`Marks: ${question.marks}`, 170, yPosition - 5, { align: 'right' });
        
        // Add space between questions
        yPosition += 10;
        
        // Add new page if needed
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
      });
      
      // Save the PDF
      doc.save(`${record.subjectName}-${record.className}-${record.examType}.pdf`);
      message.success('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  const columns = [
    {
      title: 'Subject',
      dataIndex: 'subjectName',
      key: 'subjectName',
    },
    {
      title: 'Class',
      dataIndex: 'className',
      key: 'className',
    },
    {
      title: 'Question Type',
      dataIndex: 'questionType',
      key: 'questionType',
    },
    {
      title: 'Marks Per Question',
      dataIndex: 'marksPerQuestion',
      key: 'marksPerQuestion',
    },
    {
      title: 'Question Count',
      dataIndex: 'questionCount',
      key: 'questionCount',
    },
    {
      title: 'Exam Type',
      dataIndex: 'examType',
      key: 'examType',
      render: (text) => text.charAt(0).toUpperCase() + text.slice(1)
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div className="d-flex gap-2">
          <button 
            onClick={() => handleDownloadPDF(record)}
            className="p-1.5 bg-white rounded-full hover:bg-gray-100"
            title="Download PDF"
          >
            <FileDown size={16} className="text-blue-600" />
          </button>
        </div>
      ),
    }
  ];

  const closePaperDetailsModal = () => {
    setPaperDetailsModal(false);
    setSelectedPaper(null);
  };

  return (
    <div className="container">
      <div className={`row justify-content-between ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className="col-lg-4  mb-3">
          <input
            type="text"
            placeholder={t('searchHear')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className={`w-full p-2 border rounded ${isArabic ? "text-end" : "text-start"}`}
          />
        </div>
        <div className="col-lg-8 mb-3 d-flex gap-3 justify-content-end justify-content-md-start">
          <button
            className={`btn w-50 ${activeTab === 'generate' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('generate')}
          >
            {t("GeneratePaper")}
            
          </button>
          <button
            className={`btn w-50 ${activeTab === 'view' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('view')}
          >
            {t("ViewPaper")}
          </button>
          <button
            className={`text-capitalize fs-6 gap-3 w-50  d-flex justify-content-arround align-items-center btnWithIcon bg-main ${isArabic ? "flex-row-reverse" : ""}`}
            onClick={handleAddNewExamClick}
          >
            <span className="px-1 py-1 fw-4 rounded p-0 addButtonSty">
              <Plus />
            </span>
            <span>{t('addNew')}</span>
          </button>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Add Book Modal"
      >
        <AddBook onRequestClose={closeModal} fetchBooks={fetchBooks} />
      </Modal>

      <Modal
        isOpen={generateExamModal}
        onRequestClose={closeGenerateExamModal}
        style={customStyles}
        contentLabel="Generate Exam Modal"
      >
        <GenerateExam onRequestClose={closeGenerateExamModal} selectedBookId={selectedExamId} />
      </Modal>

      <Modal
        isOpen={editModalIsOpen}
        onRequestClose={closeEditModal}
        style={customStyles}
        contentLabel="Edit Book Modal"
      >
        <EditBook onRequestClose={closeEditModal} examId={selectedExamId} />
      </Modal>

      <AntModal
        title="Delete Book"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={deleteLoading}
        okText="Yes"
        cancelText="No"
      >
        <p>Are you sure you want to delete this book?</p>
      </AntModal>

      <AntModal
        title="Paper Details"
        open={paperDetailsModal}
        onCancel={closePaperDetailsModal}
        footer={null}
        width={800}
      >
        {selectedPaper && (
          <div className="p-4">
            <div className="mb-4">
              <h4>Paper Information</h4>
              <p><strong>Subject:</strong> {selectedPaper.subjectName}</p>
              <p><strong>Class:</strong> {selectedPaper.className}</p>
              <p><strong>Exam Type:</strong> {selectedPaper.examType}</p>
              <p><strong>Created At:</strong> {new Date(selectedPaper.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <h4>Questions</h4>
              {selectedPaper.paperJson.map((question, index) => (
                <div key={index} className="mb-3 p-3 border rounded">
                  <p><strong>Question {index + 1}:</strong> {question.question}</p>
                  <p><strong>Marks:</strong> {question.marks}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </AntModal>

      {activeTab === 'generate' ? (
        <>
          <div className="row mt-5">
            {books.map((item) => (
              <div className="col-lg-3 col-md-4 col-sm-6 cursor-pointer mt-3" key={item.id}>
                <div className="w-100 h-[35vh] relative duration-300 rounded-md">
                  <img src={book} className="card-img-top w-100 h-100 rounded-md" alt="Book" />
                  <div className="card-img-overlay d-flex flex-column justify-content-center pt-7">
                    <h5 className="card-title text-white text-center fw-bold">{item.title}</h5>
                    {/* <p className="card-text text-white text-center fw-bold">{item.subject}</p> */}
                  </div>
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item.id);
                      }}
                      className="p-1.5 bg-white rounded-full hover:bg-gray-100"
                    >
                      <Edit size={16} className="text-blue-600" />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      className="p-1.5 bg-white rounded-full hover:bg-gray-100"
                    >
                      <Trash2 size={16} className="text-red-600" />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerate(item.id);
                      }}
                      className="p-1.5 bg-white rounded-full hover:bg-gray-100"
                    >
                      <FileText size={16} className="text-green-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-center mt-4 mb-4">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalBooks}
              onChange={(page, pageSize) => {
                setCurrentPage(page);
                setPageSize(pageSize);
              }}
              showSizeChanger
              showTotal={(total) => `Total ${total} books`}
            />
          </div>
        </>
      ) : (
        <Table 
          columns={columns}
          dataSource={savedPapers}
          loading={papersLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalPapers,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} papers`
          }}
        />
      )}
    </div>
  );
};

export default ExamAutomation;
